import { graphql } from 'gatsby';
import { Image } from '~components/Image';
import { Video } from '~components/Video';
import { Lottie } from '~components/Lottie';

import type { ImageComponentProps, ImageElementAttributes, ImageType } from '~components/Image';
import type { VideoComponentProps, VideoElementAttributes, VideoType } from '~components/Video';
import type { LottieProps as LottieComponentProps, LottiePlayerProps, LottieType } from '~components/Lottie';
import type { SharedProperties } from '~utils/types';

export type MixedMediaType = ImageType | VideoType | LottieType;

type MixedMediaProps = Omit<SharedProperties<ImageElementAttributes, VideoElementAttributes>, 'src'> & {
  media: MixedMediaType;
  image?: Omit<ImageComponentProps, 'image'> &
    Exclude<ImageElementAttributes, VideoElementAttributes & LottiePlayerProps>;
  video?: Omit<VideoComponentProps, 'video'> &
    Exclude<VideoElementAttributes, ImageElementAttributes & LottiePlayerProps>;
  lottie?: Omit<LottieComponentProps, 'lottie'> &
    Exclude<LottiePlayerProps, ImageElementAttributes & VideoElementAttributes>;
  src?: string | object;
};

export const MixedMedia: React.FC<MixedMediaProps> = ({ media, image, video, lottie, src, ...props }) => {
  const { contentType } = media.file;
  if (contentType.startsWith('image'))
    return <Image {...props} {...image} image={media as ImageType} src={src as string | undefined} />;
  if (contentType.startsWith('video'))
    return (
      <Video
        {...props}
        {...video}
        video={media as VideoType}
        src={src as string | undefined}
        autoPlay
        muted
        loop
        playsInline
      />
    );
  if (contentType === 'application/json')
    return <Lottie {...props} {...lottie} lottie={media as LottieType} src={src} />;
  return <></>;
};

export const query = graphql`
  fragment MixedMedia on ContentfulAsset {
    contentful_id
    title
    file {
      url
      contentType
      details {
        image {
          width
          height
        }
      }
    }
  }
`;
